/*
 * Copyright (c) Nexign, JSC, 2020-2023.
 */
/**
 * Action Conflicts Parser helper
 *
 * @author Aleksandr Perkhunov
 * @author Evgeniy Naumov
 * @author Ilya Baranchikov
 * @version 1.27.0
 * @since 1.10.0
 */

import { isCollectiveProductCustomConflict } from '@/main/pages/PageCollectiveProduct/utils/isCollectiveProductCustomConflict';
import { EProductsPageFrom } from '@/main/pages/PageProducts/tariffsAndServicesConstants';
import { RoutePath } from '@/main/routes/constants/routesConstants';
import { colors } from '@/main/theme';

import {
    EActionConflictTypes,
    IActionConflictItem,
    IActionConflicts,
} from '../../../pages/WizardProductOfferings/wizardProductOfferingsTypes';
import { ETextBlockStatus } from '../../common/TextBlockStatus/textBlockStatusTypes';

export interface IActionConflictsParsed<T = Exclude<ETextBlockStatus, ETextBlockStatus.CHANGE>> {
    status: T;
    text: string;
}

export enum ECustomNumberConflictCodes {
    TARIFF_WITH_GROUP = 'group-of-subscribers-is-connected-to-tariff',
    SUBSCRIBER_ALREADY_IN_GROUP = 'subscriber-has-subscription-group',
}

export interface IActionConflictsTranslation {
    [EActionConflictTypes.ERROR]: string;
    [EActionConflictTypes.WARNING]: string;
    tCustomErrors?: {
        willBeExcluded: string;
        errors: Record<ECustomNumberConflictCodes, string>;
    };
}

function getActionConflictsParsed<T extends ETextBlockStatus>(
    actionConflicts: IActionConflicts,
    translation: IActionConflictsTranslation,
    pageFrom?: EProductsPageFrom,
): IActionConflictsParsed<T> {
    const withError = Object.values(actionConflicts).filter((item) =>
        item.find((conflict: IActionConflictItem) => conflict.type === EActionConflictTypes.ERROR),
    );

    const withWarning = Object.values(actionConflicts).filter(
        (item) => !item.find((conflict: IActionConflictItem) => conflict.type === EActionConflictTypes.ERROR),
    );

    let text = '';
    if (withError.length) {
        const customMessages: string[] = [];
        text += `<h3>${translation[EActionConflictTypes.ERROR]}</h3>`;

        withError.forEach((item) => {
            item.forEach((conflict: IActionConflictItem) => {
                if (isCollectiveProductCustomConflict(conflict) && translation.tCustomErrors) {
                    const customMessage = translation.tCustomErrors.errors[conflict.code as ECustomNumberConflictCodes];
                    if (customMessages.includes(customMessage)) return;
                    customMessages.push(customMessage);
                    const customMessageArray = customMessage.split(' ');
                    const lastElement = customMessageArray.pop();
                    text += `<p>${customMessageArray.join(' ')} <a href="${
                        RoutePath.collectiveProduct
                    }" style="color: ${
                        colors.linkDefaultColor
                    }" onmouseenter="this.style.textDecoration='underline'" onmouseleave="this.style.textDecoration='none'">${lastElement}</a>
                            </p><p>${translation.tCustomErrors.willBeExcluded}</p>`;
                } else if (conflict.type === EActionConflictTypes.ERROR) {
                    text += `<p>${conflict.message}</p>`;
                }
            });
        });
    }
    if (withWarning.length) {
        text += `<h3>${translation[EActionConflictTypes.WARNING]}</h3>`;

        withWarning.forEach((item) => {
            item.forEach((conflict: IActionConflictItem) => {
                if (conflict.type === EActionConflictTypes.WARNING) {
                    text += `<p>${conflict.message}</p>`;
                }
            });
        });
    }

    let status: ETextBlockStatus;

    if (!withError.length && !withWarning.length && pageFrom !== EProductsPageFrom.SUBSCRIBER_ID_CHANGE_MEGA_POWERS) {
        status = ETextBlockStatus.OK;
    } else if (pageFrom === EProductsPageFrom.SUBSCRIBER_ID_CHANGE_MEGA_POWERS) {
        status = ETextBlockStatus.CHANGE;
    } else if (!withError.length) {
        status = ETextBlockStatus.WARNING;
    } else if (withWarning.length) {
        status = ETextBlockStatus.PARTIALLY;
    } else {
        status = ETextBlockStatus.ERROR;
    }

    return {
        status,
        text,
    } as IActionConflictsParsed<T>;
}

export default getActionConflictsParsed;
